import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchWikiItems } from 'services/productWikiServices';
import { WikiSearchResult } from './searchSection.types';

interface SearchOption {
  label: string;
  value: string;
  type: 'molecule' | 'concept';
  gfc?: string;
  molecule_name?: string;  // For APIFDF filtering
  strength?: string;  // For APIFDF filtering
}

export const useSearchSectionLogic = () => {
  const [selectedOption, setSelectedOption] = useState<SearchOption | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const navigate = useNavigate();
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup on component unmount (cancel any ongoing request)
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Fetch search suggestions with debounce
   */
  const fetchSearchSuggestions = async (inputValue: string): Promise<SearchOption[]> => {
    // Cancel any ongoing request if there is one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create a new AbortController instance for the new request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    return new Promise(resolve => {
      setTimeout(async () => {
        try {
          if (!inputValue || inputValue.trim().length < 2) {
            resolve([]);
            return;
          }

          const response = await searchWikiItems(inputValue.trim(), controller.signal);

          const options: SearchOption[] = response.data.map((item: WikiSearchResult) => ({
            label: item.label,
            value: item.value,
            type: item.type,
            gfc: item.gfc,
            molecule_name: item.molecule_name,  // For APIFDF filtering
            strength: item.strength,  // For APIFDF filtering
          }));

          resolve(options);
        } catch (error) {
          // Don't resolve error if request was aborted
          if (error instanceof Error && error.name === 'CanceledError') {
            resolve([]);
            return;
          }
          resolve([]);
        }
      }, 500); // Delay of 500ms for debouncing
    });
  };

  /**
   * Handle input value change
   */
  const handleInputChange = (newValue: string, _actionMeta?: { action: string }) => {
    // Always update input value when typing
    setInputValue(newValue);
  };

  /**
   * Handle clear input
   */
  const handleClearInput = () => {
    setSelectedOption(null);
    setInputValue('');
  };

  /**
   * Handle selection from AsyncSelect dropdown
   */
  const handleSearchSelect = (option: SearchOption | null) => {
    if (!option) {
      setSelectedOption(null);
      setInputValue('');
      return;
    }

    // Set the values
    setSelectedOption(option);
    setInputValue(option.label);

    // Navigate based on type
    if (option.type === 'molecule') {
      // Molecules go to molecule-search page
      const searchPath = `/bdportal/product-wiki/molecule-search?item=${encodeURIComponent(option.value)}&type=${option.type}&label=${encodeURIComponent(option.label)}`;
      navigate(searchPath);
    } else if (option.type === 'concept' && option.gfc) {
      // Concepts go directly to product-analysis page (overview tab)
      // Include molecule and strength parameters for APIFDF filtering
      const params = new URLSearchParams();
      params.set('gfc', option.gfc);
      params.set('label', option.label);
      if (option.molecule_name) {
        params.set('item', option.molecule_name);  // Use 'item' for backward compatibility
        params.set('molecule', option.molecule_name);  // Also add 'molecule' for explicitness
      }
      if (option.strength) {
        params.set('strength', option.strength);
      }
      const searchPath = `/bdportal/product-wiki/product-analysis?${params.toString()}`;
      navigate(searchPath);
    }
  };

  /**
   * Handle search button click - Navigate based on selected option
   */
  const handleSearchSubmit = () => {
    if (!selectedOption) {
      return;
    }

    // Navigate based on type
    if (selectedOption.type === 'molecule') {
      // Molecules go to molecule-search page
      const searchPath = `/bdportal/product-wiki/molecule-search?item=${encodeURIComponent(selectedOption.value)}&type=${selectedOption.type}&label=${encodeURIComponent(selectedOption.label)}`;
      navigate(searchPath);
    } else if (selectedOption.type === 'concept' && selectedOption.gfc) {
      // Concepts go directly to product-analysis page (overview tab)
      // Include molecule and strength parameters for APIFDF filtering
      const params = new URLSearchParams();
      params.set('gfc', selectedOption.gfc);
      params.set('label', selectedOption.label);
      if (selectedOption.molecule_name) {
        params.set('item', selectedOption.molecule_name);  // Use 'item' for backward compatibility
        params.set('molecule', selectedOption.molecule_name);  // Also add 'molecule' for explicitness
      }
      if (selectedOption.strength) {
        params.set('strength', selectedOption.strength);
      }
      const searchPath = `/bdportal/product-wiki/product-analysis?${params.toString()}`;
      navigate(searchPath);
    }
  };

  /**
   * Handle suggestion tag click
   */
  const handleSuggestionClick = (suggestion: {
    text: string;
    searchTerm: string;
    type?: string;
    gfc?: string;
    molecule_name?: string;
    strength?: string;
  }) => {
    // Create option from suggestion
    const option: SearchOption = {
      label: suggestion.text,
      value: suggestion.searchTerm,
      type: (suggestion.type as 'molecule' | 'concept') || 'molecule',
      gfc: suggestion.gfc,
      molecule_name: suggestion.molecule_name,
      strength: suggestion.strength,
    };

    setSelectedOption(option);
    setInputValue(suggestion.text);

    // Navigate based on type
    setTimeout(() => {
      if (option.type === 'molecule') {
        // Molecules go to molecule-search page
        const searchPath = `/bdportal/product-wiki/molecule-search?item=${encodeURIComponent(suggestion.searchTerm)}&type=${option.type}&label=${encodeURIComponent(suggestion.text)}`;
        navigate(searchPath);
      } else if (option.type === 'concept' && option.gfc) {
        // Concepts go directly to product-analysis page (overview tab)
        // Include molecule and strength parameters for APIFDF filtering
        const params = new URLSearchParams();
        params.set('gfc', option.gfc);
        params.set('label', suggestion.text);
        if (option.molecule_name) {
          params.set('item', option.molecule_name);  // Use 'item' for backward compatibility
          params.set('molecule', option.molecule_name);  // Also add 'molecule' for explicitness
        }
        if (option.strength) {
          params.set('strength', option.strength);
        }
        const searchPath = `/bdportal/product-wiki/product-analysis?${params.toString()}`;
        navigate(searchPath);
      }
    }, 300);
  };

  return {
    selectedOption,
    inputValue,
    fetchSearchSuggestions,
    handleSearchSelect,
    handleInputChange,
    handleClearInput,
    handleSearchSubmit,
    handleSuggestionClick,
  };
};
